import { type MaybeRefOrGetter, onUnmounted, ref, toValue, watch } from "vue";

const CEILING = 0.92;
const COMPLETE_DURATION = 420;

export function useCreepProgress(active: MaybeRefOrGetter<boolean>, expectedDuration?: MaybeRefOrGetter<number | null | undefined>) {
	const progress = ref(0);
	let frame: number | null = null;
	let startedAt = 0;
	let completingFrom: number | null = null;
	let completingAt = 0;

	const cancel = () => {
		if (frame === null) return;
		cancelAnimationFrame(frame);
		frame = null;
	};

	const tick = (now: number) => {
		if (completingFrom !== null) {
			const ratio = Math.min((now - completingAt) / COMPLETE_DURATION, 1);
			progress.value = completingFrom + (1 - completingFrom) * ratio;
			frame = ratio < 1 ? requestAnimationFrame(tick) : null;
			return;
		}

		const duration = toValue(expectedDuration) || 9000;
		const elapsed = now - startedAt;
		progress.value = CEILING * (1 - Math.exp(-elapsed / duration));
		frame = requestAnimationFrame(tick);
	};

	const complete = () => {
		if (completingFrom !== null) return;
		completingFrom = progress.value;
		completingAt = performance.now();
		cancel();
		frame = requestAnimationFrame(tick);
	};

	watch(
		() => toValue(active),
		(isActive) => {
			cancel();
			completingFrom = null;

			if (!isActive) {
				progress.value = 0;
				return;
			}

			progress.value = 0;
			startedAt = performance.now();
			frame = requestAnimationFrame(tick);
		},
		{ immediate: true },
	);

	onUnmounted(cancel);

	return { progress, complete };
}
